/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Seo, Footer, KoinosWordmark } from '@components/systems'
import { MonolithProductNav } from '@components/systems/monolith/MonolithProductNav'
import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'
import { buildProductJsonLd, buildBreadcrumbJsonLd, formatAmount } from '@utils'
import { SEO_SITE, getSeoEntry } from '@src/configs/seo.config'
import { deviceStats } from '@src/configs/devices.generated'
import { useDeviceIndex } from '@components/systems/monolith/useDeviceIndex'
import { useDisplayCurrency } from '@components/systems/monolith/useDisplayCurrency'

import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '@redux/index'
import { SeasonProduct, SeasonProductType } from '@schemas'

import heroImg from '@public/images/monolith/koinos8-top-front-screen-m7.png'
import detailLetteringImg from '@public/images/monolith/koinos8-detail-lettering.png'
import productAngleImg from '@public/images/monolith/koinos8-top-angle-screen-m54.png'
import patentFuzzImg from '@public/images/monolith/patent-us3213181.png'
import patentWahImg from '@public/images/monolith/patent-us3530224.png'
import floorRigImg from '@public/images/monolith/floor-rig.jpg'
import rearImg from '@public/images/monolith/koinos8-rear-front.png'
import m7MainImg from '@public/images/monolith/m7-main.png'
import m54ChainImg from '@public/images/monolith/m54-chain.png'
import m19EditImg from '@public/images/monolith/m19-edit.png'
import m61PedalsImg from '@public/images/monolith/m61-pedals.png'
import m8SetupImg from '@public/images/monolith/m8-setup.png'
import m20MessagesImg from '@public/images/monolith/m20-messages.png'
import m40TonexImg from '@public/images/monolith/m40-tonex.png'
import m9SetlistImg from '@public/images/monolith/m9-setlist.png'
import studioEditorImg from '@public/images/monolith/studio-switch-presets.jpg'
import mobileEditorImg from '@public/images/monolith/mobile-s26u-01.png'

/**
 * Koinos PLS 8 제품 페이지 — 헤리티지 재기획 (claudeFiles/judahsWeb/SITEMAP_PLAN.md §31).
 *
 * 페이지 전체가 한 이야기다: "스위치가 어디에 있었나".
 * 페달마다 제 스위치(특허 도면 1965·1970) → 1980 Bob Bradshaw 가 한 컨트롤러로 모았다 →
 * RJM 이 이어받았다 → 결정이 노트북으로 새 나갔다 → 결정을 기기 위 화면으로 되돌렸다 →
 * 이제 당신의 페달보드.
 *
 * 규칙
 * - 숫자는 1980 하나만 크게 쓴다. 우리 연도는 숫자로 쓰지 않는다. "Since 1980" 금지 (§31.2).
 * - 다른 회사 이름이 나오는 문단 바로 아래에 상표·비제휴 고지를 둔다. 제휴로 읽히면 안 된다.
 * - RJM 은 "배웠다" 까지만 쓴다. 동등성("같은 소리")·모델명·측정 방법은 웹에 쓰지 않는다.
 * - 루프 사양에 "true bypass" 를 쓰지 않는다. 루프는 아날로그 매트릭스로 스위칭한다 (§31.9).
 * - 특허 도면은 공공 영역(USPTO). 서명 블록은 잘라냈다. 출처 줄은 달지 않는다.
 * - 사양은 매뉴얼 부록 A 가 정본이다. 값이 갈리면 매뉴얼을 따른다.
 */
const MonolithPls8Page = () => {
	// hook
	const navigate = useNavigate()

	const seasonProductList: SeasonProductType[] = useAppSelector(
		state => state.shop.productList,
	)

	const thisProduct: SeasonProductType = React.useMemo(() => {
		const filterRes = seasonProductList.filter(
			aProduct => (aProduct.productCode ?? '').toUpperCase() === 'PLS8',
		)
		return filterRes.length === 0 ? new SeasonProduct() : filterRes[0]
	}, [seasonProductList])

	const isPreOrder: boolean = thisProduct.seasonUsd <= 0

	/*
		제품 페이지가 말하는 값은 **판매가 하나**다 (2026-09-15 가격 구조 결정). 얼리버드·프리오더 값은
		`/shop/pls8` 에서만 — 처음 본 숫자로 제품 등급이 매겨지므로, 이 제품이 무엇인가를 말하는
		페이지에서는 $1,000 만 세운다. 판매가 = `seasonUsd`, 구매 패널의 List price 와 같은 값.

		한국 방문자에게는 원화만 — ₩1,500,000 (`seasonKrw`, 부가세 포함). 달러를 같이 두지 않는다
		(PRICING §6 · §7.3, 2026-09-16). JSON-LD 의 offers 는 크롤러 몫이라 USD 그대로다.
	*/
	const currency = useDisplayCurrency()
	const listPrice: string =
		currency === 'KRW' && thisProduct.seasonKrw > 0
			? formatAmount('KRW', thisProduct.seasonKrw)
			: thisProduct.seasonUsd > 0
			? formatAmount('USD', thisProduct.seasonUsd)
			: ''

	/**
	 * 딕셔너리 숫자는 손으로 적지 않는다. `/works-with` 와 같은 런타임 색인(`public/works-with/index.json`,
	 * `npm run deploy:devices` 가 S3 정본에서 굽는다)을 읽고, 도착 전·실패 시에는 번들 사본으로 그린다.
	 * 파라미터는 천 단위로 내려 "over N" 이 항상 참이 되게 한다.
	 */
	const deviceIndex = useDeviceIndex()
	const dictStats = deviceIndex ? deviceIndex.stats : deviceStats
	const dictDevices = dictStats.devices.toLocaleString('en-US')
	const dictParameters = (Math.floor(dictStats.parameters / 1000) * 1000).toLocaleString(
		'en-US',
	)

	const jsonLd = React.useMemo(
		() => [
			buildProductJsonLd({
				name: 'Koinos PLS 8',
				description: getSeoEntry('/products/pls8').description,
				path: '/products/pls8',
				image: `${SEO_SITE.origin}/images/monolith/koinos8-top-front-screen-m7.png`,
				product: thisProduct,
				preOrder: isPreOrder,
				sku: 'PLS8',
			}),
			buildBreadcrumbJsonLd([
				{ name: 'Home', path: '/' },
				{ name: 'Products', path: '/products' },
				{ name: 'Koinos PLS 8', path: '/products/pls8' },
			]),
		],
		[thisProduct, isPreOrder],
	)

	const goBuy = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault()
		navigate('/shop/pls8')
	}

	const goStudio = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault()
		navigate('/software/judahs-studio')
	}

	const goMobile = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault()
		navigate('/software/judahs-studio-mobile')
	}

	const plateList: { src: string; alt: string; caption: string }[] = [
		{
			src: patentFuzzImg,
			alt: 'Drawing from U.S. Patent 3,213,181, 1965: a guitar, a pedal and an amplifier, and the pedal circuit with its on and off switch',
			caption: '1965 · U.S. Patent 3,213,181 · Tone modifier',
		},
		{
			src: patentWahImg,
			alt: 'Drawing from U.S. Patent 3,530,224, 1970: a foot-controlled circuit with a switch marked Straight and Wow-Wow',
			caption: '1970 · U.S. Patent 3,530,224 · Foot-controlled preference circuit',
		},
	]

	const screenList: { src: string; alt: string; caption: string }[] = [
		{
			src: m7MainImg,
			alt: 'Performance view',
			caption: 'Performance view: bank, page, chain, tempo.',
		},
		{
			src: m54ChainImg,
			alt: 'Chain order',
			caption: 'Chain order, dragged with a finger.',
		},
		{
			src: m19EditImg,
			alt: 'Preset edit',
			caption: 'Preset edit: input, buffer, loops.',
		},
		{
			src: m61PedalsImg,
			alt: 'Pedal manager',
			caption: 'Pedal manager: loops, MIDI, relay and ToneX in one list.',
		},
		{
			src: m8SetupImg,
			alt: 'Manual mode',
			caption: 'Manual mode: ten switches, one pedal each.',
		},
		{
			src: m20MessagesImg,
			alt: 'Switch messages',
			caption: 'One switch, its messages: PC, CC, notes.',
		},
		{
			src: m40TonexImg,
			alt: 'ToneX One message',
			caption: 'ToneX One over USB: slot, type, blocks.',
		},
		{ src: m9SetlistImg, alt: 'Setlist', caption: '1,200 songs. 100 setlists.' },
	]

	// 매뉴얼 부록 A 기준. 그룹 라벨로 나눠 스캔하게 한다.
	const specGroupList: { group: string; rows: { label: string; value: string }[] }[] = [
		{
			group: 'Audio',
			rows: [
				{ label: 'Signal path', value: '100% analog' },
				{
					label: 'Loops',
					value:
						'8 loops: Loops 1–5 mono, Loops 6–8 stereo (or each split into two mono loops, up to 11 mono loops)',
				},
				{
					label: 'Insert',
					value: 'Buffered stereo insert loop, separate from the 8 loops',
				},
				{
					label: 'Routing',
					value:
						'Matrix switching, loop order stored per preset, up to 13 chain positions (11 mono loops plus the insert split in two)',
				},
				{
					label: 'Input buffer',
					value: 'Switchable, 500 kΩ or 1 MΩ input selected on screen',
				},
				{
					label: 'Fuzz loop',
					value: 'Input buffer bypassed while a fuzz loop is on and first in the chain',
				},
				{
					label: 'Inputs',
					value: 'Guitar In · Aux In (shares its socket with Tuner Out)',
				},
				{
					label: 'Outputs',
					value:
						'Out L · Out R, two separate mono jacks · Tuner Out (shares its socket with Aux In)',
				},
			],
		},
		{
			group: 'Control',
			rows: [
				{
					label: 'Footswitches',
					value:
						'10 built in · 8 two-switch combinations · 2 external, up to 20 assignable',
				},
				{ label: 'Expression', value: 'Expression pedal input, TRS jack' },
				{ label: 'External switch', value: 'Single or dual external switches, TRS jack' },
				{ label: 'Relay out', value: 'Amp channel switching, tip and ring independent' },
				{
					label: 'Tuner',
					value:
						'Built in, full screen and a mini tuner while playing · Tuner Out always on',
				},
			],
		},
		{
			group: 'MIDI & USB',
			rows: [
				{
					label: 'MIDI',
					value:
						'DIN in · DIN out · TRS out (Type A, Type B, Tip active, Ring active) · USB',
				},
				{
					label: 'USB',
					value:
						'2 × host for USB-MIDI devices or ToneX One · 1 × device for the editor and USB-MIDI',
				},
				{
					label: 'MIDI dictionary',
					value: `${dictDevices} devices and over ${dictParameters} parameters, held on the unit`,
				},
			],
		},
		{
			group: 'Wireless',
			rows: [
				{ label: 'Bluetooth', value: 'Bluetooth LE, for page turning and the editor' },
				{ label: 'Wi-Fi', value: '2.4 GHz access point, for the editor' },
				{
					label: 'AirMIDI',
					value: 'Wireless MIDI link to other Judahs devices, up to 20 receivers',
				},
				{ label: 'Antenna', value: 'External, 2 dBi, rear panel' },
			],
		},
		{
			group: 'Display & memory',
			rows: [
				{ label: 'Display', value: '8.88-inch IPS touch, 1920 × 480' },
				{
					label: 'Memory',
					value: '150 banks · 1,200 songs · 100 setlists · 20 messages per action',
				},
			],
		},
		{
			group: 'System',
			rows: [
				{
					label: 'Processing',
					value:
						'3 dual-core processors: one for the display, switching and MIDI, and one for each USB host port',
				},
				{ label: 'Boot', value: '0.8 seconds' },
				{
					label: 'Editor',
					value:
						'Judahs Studio on macOS and Windows. Judahs Studio Mobile on iOS and Android.',
				},
			],
		},
		{
			group: 'Physical',
			rows: [
				{ label: 'Power', value: '9 V DC, center negative, 1 A' },
				{ label: 'Dimensions', value: '290 × 120 × 86 mm (W × D × H)' },
				{ label: 'Weight', value: '1.44 kg' },
			],
		},
	]

	return (
		<View css={style}>
			<Seo jsonLd={jsonLd} />

			{/* 2026-09-10: 라인업 서브 네비. 제품 5종 사이를 오가는 길이라 PLS 8 에도 있어야
			    목록이 끊기지 않는다 (SITEMAP_PLAN §16.3) */}
			<MonolithProductNav activeCode="pls8" />

			{/* 00 hero — Home 은 가운데 정렬 전폭이라 여기는 좌우 분할 + 리드 + Buy 로 갈라진다 */}
			<View className="mnWrap mnHero">
				<View className="mnHeroText">
					<span className="mnKicker">Programmable loop switcher · MIDI controller</span>
					<KoinosWordmark as="h1" className="mnHeroWordmark" />
					<p className="mnHeroTagline">Eight loops, set up on the unit itself.</p>
					<p className="mnHeroLede">
						Eight loops and a stereo insert, set up on an 8.88-inch touch display, with a
						choice of 500 kΩ or 1 MΩ at the input. You do not need a laptop for any of it.
					</p>
					<View className="mnHeroAction">
						<a className="mnCta" href="/shop/pls8" onClick={goBuy}>
							Buy
						</a>
						{/* 값은 숫자 하나. 형용사·"from"·배지 없이 (PERSONA §1) */}
						{listPrice !== '' && <span className="mnListPrice">{listPrice}</span>}
					</View>
				</View>
				<View className="mnHeroImageView">
					<img
						src={heroImg}
						alt="Koinos PLS 8, front view with the display on"
						className="mnHeroImage"
					/>
				</View>
			</View>

			<View className="mnWrap">
				{/* 01 1980 — 페이지의 시간 기준점. 숫자 옆에 이름을 붙여 우리 연도로 읽히지 않게 한다 */}
				<View className="mnBand mnYear">
					<p className="mnYearNumber">1980</p>
					<View className="mnYearText">
						<p className="mnYearLead">Bob Bradshaw founded Custom Audio Electronics.</p>
						<p className="mnBody">
							Each effect went into its own loop, and the switching moved to one
							controller at the player’s feet.
						</p>
					</View>
				</View>

				{/* 02 before 1980 — 공공 영역 특허 도면. 스위치가 페달마다 따로 있었다 */}
				<View className="mnBand mnBefore">
					<span className="mnKicker">Before 1980</span>
					<p className="mnLede mnBeforeLede">Each pedal came with its own switch.</p>
					<p className="mnBody mnBeforeBody">
						The fuzz had one on top, and the wah had a switch marked Straight and Wow-Wow.
						Five pedals meant five switches in five places.
					</p>
					<View className="mnPlates">
						{plateList.map(aPlate => (
							<figure key={aPlate.caption} className="mnPlate">
								<View className="mnPlateFrame">
									<img src={aPlate.src} alt={aPlate.alt} className="mnPlateImage" />
								</View>
								<figcaption className="mnPlateCaption">{aPlate.caption}</figcaption>
							</figure>
						))}
					</View>
				</View>

				{/* 03 lineage — 기린다. 배웠다까지만 말하고, 이름이 나온 문단 바로 아래에 비제휴 고지 */}
				<View className="mnBand mnCentered">
					<span className="mnKicker">The part we inherited</span>
					<h2 className="mnHeading">None of this starts with us.</h2>
					<p className="mnBody mnBodyNarrow">
						Bob Bradshaw started building switching systems in 1980, and Custom Audio
						Electronics still builds on that plan. RJM carried the standard forward and
						put MIDI control in the same box. We spent months studying their work before
						the first board of our own came back from the factory. Koinos PLS 8 would not
						exist without either of them.
					</p>
					<p className="mnNotice">
						Custom Audio Electronics and RJM are trademarks of their respective owners and
						are not affiliated with Judahs.
					</p>
					<p className="mnEmphasis">We set out to learn that sound. Not to beat it.</p>
				</View>
			</View>

			{/* 03b 제품 — 헤리티지가 끝나고 처음 나오는 실물. 잘린 쪽(왼쪽)을 화면 가장자리에 붙인다 */}
			<View className="mnBleed mnBleedLeft">
				<img
					src={detailLetteringImg}
					alt="Koinos PLS 8, close-up of the lettering on the front face"
					className="mnBleedImage"
				/>
			</View>

			<View className="mnWrap">
				{/* 04 the ritual — 1980 을 되부른다. 루프는 발밑에 남고 결정이 노트북으로 새 나갔다 */}
				<View className="mnBand mnRitual">
					<View>
						<p className="mnLede">
							Soundcheck is running short. One preset needs one small change. Out comes
							the laptop.
						</p>
						<p className="mnBody mnBodyAfterLede">
							The loops are still at the player’s feet, as they were in 1980. The
							decisions moved to a laptop.
						</p>
					</View>
					<View className="mnFigure">
						<img
							src={floorRigImg}
							alt="Pedals and cables on a floor"
							className="mnFigureImage"
						/>
						<span className="mnFigureCaption">
							The old way: one box, one job, many cables
						</span>
					</View>
				</View>

				{/* 05 the turn */}
				<View className="mnBand mnCentered">
					<span className="mnKicker">The part we did not</span>
					<h2 className="mnHeading mnHeadingWide">
						What we chose not to inherit is the place where decisions happen.
					</h2>
					<p className="mnBody mnBodyNarrower">
						Setup lives on an 8.88-inch touch display on the unit, so a change at
						rehearsal or mid-show does not need a laptop.
					</p>
				</View>

				{/* 05b input impedance — 매뉴얼 편지 "Even the input impedance is yours to decide". 릴레이가 회로에서 부하를 바꾼다(HARDWARE §7.4 II_RELAY) */}
				<View className="mnBand mnImpedance">
					<span className="mnKicker">Input impedance</span>
					<h2 className="mnHeading mnImpHeading">
						Even the input impedance is yours to decide.
					</h2>
					<View className="mnImpValues">
						<View className="mnImpValue">
							<p className="mnImpNumber">
								500<span className="mnImpUnit">kΩ</span>
							</p>
							<span className="mnImpLabel">Warmer · Default</span>
						</View>
						<View className="mnImpValue">
							<p className="mnImpNumber">
								1<span className="mnImpUnit">MΩ</span>
							</p>
							<span className="mnImpLabel">Brighter</span>
						</View>
					</View>
					<p className="mnBody mnImpBody">
						The setting changes the load your pickups see. A relay switches it in the
						circuit, and you choose it on screen.
					</p>
				</View>

				{/* 06 screens */}
				<View className="mnBand">
					<span className="mnKicker mnSectionKicker">On the screen</span>
					<View className="mnScreens">
						{screenList.map(aScreen => (
							<View key={aScreen.caption} className="mnScreen">
								<img src={aScreen.src} alt={aScreen.alt} className="mnScreenImage" />
								<div className="mnScreenCaption">{aScreen.caption}</div>
							</View>
						))}
					</View>
				</View>

				{/*
					06b editor — 노트북은 필요 없다고 말해 온 페이지가 여기서 한 번 덧붙인다: 없어도 되지만
					있어도 된다. 에디터가 둘(Studio · Studio Mobile)이라는 사실과 받는 곳. 같은 화면(Switch
					presets)을 두 앱에서 나란히 놓는다. 묻지 않고 적는다 (PERSONA §1). Mobile 은 미출시 —
					`Not yet released` 만 적고 시기는 말하지 않는다.
				*/}
				<View className="mnBand mnCentered">
					<span className="mnKicker">Editor</span>
					<h2 className="mnHeading">The laptop is optional. So is the phone.</h2>
					<p className="mnBody mnBodyNarrower">
						Judahs Studio edits on a Mac or a Windows PC, and keeps the backups and the
						firmware. Judahs Studio Mobile edits on a phone or a tablet, over USB,
						Bluetooth or Wi-Fi. Both write to the same banks, songs and setlists the unit
						holds.
					</p>
					<p className="mnBody mnBodyNarrower mnEditorFree">Studio is free.</p>
					<View className="mnEditors">
						<a className="mnEditor" href="/software/judahs-studio" onClick={goStudio}>
							<View className="mnEditorFrame">
								<img
									src={studioEditorImg}
									alt="Judahs Studio on a desktop, on the Switch presets screen"
									className="mnEditorImage mnEditorImageDesktop"
								/>
							</View>
							<span className="mnEditorName">Judahs Studio</span>
							<span className="mnEditorMeta">macOS · Windows</span>
						</a>
						<a
							className="mnEditor"
							href="/software/judahs-studio-mobile"
							onClick={goMobile}
						>
							<View className="mnEditorFrame">
								<img
									src={mobileEditorImg}
									alt="Judahs Studio Mobile on a Galaxy S26 Ultra, on the Switch presets screen"
									className="mnEditorImage mnEditorImagePhone"
								/>
							</View>
							<span className="mnEditorName">Judahs Studio Mobile</span>
							<span className="mnEditorMeta">iOS · Android · Not yet released</span>
						</a>
					</View>
					<View className="mnEditorAction">
						<a className="mnCtaGhost" href="/software/judahs-studio" onClick={goStudio}>
							Download Judahs Studio
						</a>
					</View>
				</View>

				{/* 07 rear — 1980 의 원칙을 잭이 증명한다 */}
				<View className="mnBand mnCentered">
					<span className="mnKicker">Rear panel</span>
					<h2 className="mnHeading">Each effect in its own loop.</h2>
					<View className="mnRearImageView">
						<img src={rearImg} alt="Koinos PLS 8 rear panel" className="mnRearImage" />
					</View>
					<p className="mnRearCaption">
						8 send/return pairs · insert · stereo out · expression · relay · 2 × USB host
						· MIDI in/out · 9 V DC
					</p>
				</View>

				{/* 08 what we added */}
				<View className="mnBand">
					<span className="mnKicker mnSectionKicker">What we added</span>
					<View className="mnClaims">
						<View className="mnClaim">
							<h3>Reorder your pedals with a touch.</h3>
							<p>No cables. Saved per preset.</p>
						</View>
						<View className="mnClaim">
							<h3>Plug USB-MIDI gear straight in.</h3>
							<p>Two USB host ports take USB-MIDI devices without a DIN adapter.</p>
						</View>
						<View className="mnClaim">
							<h3>Forget CC numbers.</h3>
							<p>
								{dictDevices} devices and over {dictParameters} MIDI parameters, on the
								unit.
							</p>
						</View>
					</View>
				</View>
			</View>

			{/* 08b 제품 — 사양 표 앞에 전신 한 컷. 화면에 루프 설정(IN L1…L8 OUT)이 떠 있어 바로 아래 Audio 사양과 맞물린다 */}
			<View className="mnWrap mnShot">
				<img
					src={productAngleImg}
					alt="Koinos PLS 8, angled view with the loop settings on the display"
					className="mnShotImage"
				/>
			</View>

			<View className="mnWrap">
				{/* 09 specs — 매뉴얼 부록 A 정본 */}
				<View className="mnBand mnSpecBand" id="specifications">
					<span className="mnKicker mnSectionKicker">Specifications</span>
					<View className="mnSpecTable">
						{specGroupList.map(aGroup => (
							<View key={aGroup.group} className="mnSpecGroup">
								<span className="mnSpecGroupLabel">{aGroup.group}</span>
								{aGroup.rows.map(aSpec => (
									<View key={aSpec.label} className="mnSpecRow">
										<span className="mnSpecLabel">{aSpec.label}</span>
										<span className="mnSpecValue">{aSpec.value}</span>
									</View>
								))}
							</View>
						))}
					</View>
				</View>

				{/* 10 close — 계보의 다음 칸이 사는 사람이다 */}
				<View className="mnBand mnCentered mnClose">
					<h2 className="mnHeading mnHeadingClose">From today it is your pedalboard.</h2>
					{/*
						블랙 Buy 로 닫는다. 구매 화면(`/shop/pls8`)은 고스트 "product page" 로 닫혀
						두 페이지가 서로를 가리킨다 (DESIGN_SYSTEM §6.3).
					*/}
					<View className="mnCloseAction">
						<a className="mnCta" href="/shop/pls8" onClick={goBuy}>
							Buy
						</a>
						{listPrice !== '' && <span className="mnListPrice">{listPrice}</span>}
					</View>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithPls8Page

const style = css({
	...monoShared,
	position: 'relative',
	width: '100%',

	// 좌우 반반. §6.4 의 워드마크 규격(최대 96px)은 전폭 히어로 기준이라, 반 칸에 들어가도록 줄였다.
	'.mnHero': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 6fr) minmax(0, 6fr)',
		gap: 'clamp(32px, 5vw, 80px)',
		alignItems: 'center',
		// padding 단축형을 쓰면 mnWrap 의 좌우 거터가 0 이 된다. 위아래만 적는다.
		paddingTop: 'clamp(40px, 6vw, 88px)',
		paddingBottom: 'clamp(56px, 7vw, 104px)',

		'.mnHeroText': {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
		},

		'.mnHeroWordmark': {
			marginTop: 32,
			fontSize: 'clamp(34px, 4.2vw, 60px)',
			whiteSpace: 'nowrap',
			color: mono.ink,
		},

		'.mnHeroTagline': {
			marginTop: 28,
			fontSize: 'clamp(22px, 2.2vw, 32px)',
			lineHeight: 1.25,
			fontWeight: 200,
			color: mono.ink,
		},

		'.mnHeroLede': {
			marginTop: 24,
			maxWidth: 520,
			fontSize: 17,
			lineHeight: 1.7,
			fontWeight: 300,
			color: mono.dim,
		},

		'.mnHeroAction': {
			marginTop: 40,
			display: 'flex',
			alignItems: 'center',
			gap: 28,
		},

		'.mnHeroImageView': {
			width: '100%',

			'.mnHeroImage': {
				width: '100%',
				maxWidth: '100%',
				aspectRatio: '2 / 1',
				objectFit: 'cover',
				display: 'block',
				filter: 'drop-shadow(0 40px 60px rgba(20,24,32,0.16))',
			},
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',

			'.mnHeroWordmark': {
				fontSize: 'clamp(34px, 9vw, 60px)',
			},
		},
	},

	// 객체 리터럴에서 같은 키는 뒤가 통째로 이기므로 borderTop 도 같이 다시 적는다.
	'.mnBand': {
		borderTop: `1px solid ${mono.line}`,
		padding: 'clamp(72px, 9vw, 130px) 0',
	},

	'.mnCentered': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',
	},

	'.mnLede': {
		fontSize: 'clamp(26px, 2.8vw, 40px)',
		lineHeight: 1.35,
		fontWeight: 200,
		color: mono.ink,
	},

	'.mnHeading': {
		marginTop: 40,
		fontSize: 'clamp(32px, 3.6vw, 52px)',
		lineHeight: 1.15,
		fontWeight: 200,
		color: mono.ink,
		maxWidth: 900,

		'&.mnHeadingWide': { maxWidth: 1000, lineHeight: 1.2 },
		'&.mnHeadingClose': { marginTop: 0, lineHeight: 1.2 },
	},

	'.mnBody': {
		fontSize: 17,
		lineHeight: 1.7,
		fontWeight: 300,
		color: mono.dim,

		'&.mnBodyAfterLede': { marginTop: 34 },
		'&.mnBodyNarrow': { marginTop: 36, maxWidth: 720, lineHeight: 1.75 },
		'&.mnBodyNarrower': { marginTop: 36, maxWidth: 620 },
		'&.mnEditorFree': { marginTop: 8 },
		'&.mnBodyClose': { marginTop: 30, fontSize: 16, maxWidth: 560 },
	},

	'.mnEmphasis': {
		marginTop: 34,
		fontSize: 22,
		lineHeight: 1.5,
		fontWeight: 400,
		color: mono.ink,
	},

	// 비제휴 고지 — 이름이 나온 문단 바로 아래. 읽히되 문단의 무게를 뺏지 않는 크기.
	'.mnNotice': {
		marginTop: 20,
		maxWidth: 720,
		fontSize: 12,
		lineHeight: 1.6,
		fontWeight: 400,
		color: mono.faint,
	},

	// 1980 — 숫자 좌 7 / 문장 우 5. 모델라인 서체(MuseoModerno)는 워드마크 전용이라 쓰지 않는다.
	'.mnYear': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 5fr)',
		gap: 'clamp(24px, 5vw, 80px)',
		alignItems: 'start',

		'.mnYearNumber': {
			fontSize: 'clamp(112px, 19vw, 300px)',
			lineHeight: 0.8,
			fontWeight: 100,
			letterSpacing: '-0.04em',
			fontVariantNumeric: 'lining-nums tabular-nums',
			color: mono.ink,
		},

		'.mnYearText': {
			display: 'flex',
			flexDirection: 'column',
			gap: 20,
			paddingTop: 'clamp(4px, 1vw, 16px)',
			maxWidth: 460,
		},

		'.mnYearLead': {
			fontSize: 'clamp(22px, 2vw, 28px)',
			lineHeight: 1.35,
			fontWeight: 400,
			color: mono.ink,
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
		},
	},

	// 잘린 제품 컷 — 잘린 쪽을 뷰포트 가장자리에 붙인다. 원본 1600px 보다 크게 그리지 않는다(래스터 확대 금지).
	'.mnBleed': {
		overflow: 'hidden',
		padding: 'clamp(24px, 4vw, 64px) 0',

		'.mnBleedImage': {
			display: 'block',
			width: 'min(80vw, 1280px)',
			maxWidth: '100%',
			filter: 'drop-shadow(0 32px 48px rgba(20,24,32,0.14))',
		},

		// 내용 영역 1573×896 (y 432 부터). 16:9 틀에 그 띠만 남긴다.
		'&.mnBleedLeft .mnBleedImage': {
			marginRight: 'auto',
			aspectRatio: '16 / 9',
			objectFit: 'cover',
			objectPosition: '0% 62%',
		},

		'@media (max-width: 900px)': {
			'.mnBleedImage': { width: '92vw' },
		},
	},

	// 전신 컷 — mnWrap 안. padding 단축형은 거터를 지우므로 위아래만. 내용 영역 1524×713(y 445~1158)이라 2:1 로 띠만 남긴다.
	'.mnShot': {
		paddingTop: 'clamp(24px, 4vw, 64px)',
		paddingBottom: 'clamp(24px, 4vw, 64px)',

		'.mnShotImage': {
			display: 'block',
			width: '100%',
			maxWidth: 1180,
			margin: '0 auto',
			aspectRatio: '2 / 1',
			objectFit: 'cover',
			filter: 'drop-shadow(0 40px 60px rgba(20,24,32,0.16))',
		},
	},

	// 입력 임피던스 — 1980 과 같은 문법(Archivo 100 대형 숫자). 고르는 것이 요점이라 두 값은 같은 크기다.
	'.mnImpedance': {
		'.mnImpHeading': { maxWidth: 820 },

		'.mnImpValues': {
			marginTop: 'clamp(48px, 6vw, 80px)',
			display: 'grid',
			gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
			borderTop: `1px solid ${mono.ink}`,
		},

		'.mnImpValue': {
			display: 'flex',
			flexDirection: 'column',
			gap: 16,
			paddingTop: 'clamp(24px, 3vw, 40px)',

			'& + .mnImpValue': {
				borderLeft: `1px solid ${mono.line}`,
				paddingLeft: 'clamp(20px, 4vw, 56px)',
			},
		},

		'.mnImpNumber': {
			fontSize: 'clamp(64px, 11vw, 168px)',
			lineHeight: 0.9,
			fontWeight: 100,
			letterSpacing: '-0.03em',
			fontVariantNumeric: 'lining-nums tabular-nums',
			whiteSpace: 'nowrap',
			color: mono.ink,
		},

		'.mnImpUnit': {
			marginLeft: '0.12em',
			fontSize: '0.42em',
			fontWeight: 200,
			letterSpacing: 0,
		},

		'.mnImpLabel': {
			fontSize: 12,
			letterSpacing: '.2em',
			textTransform: 'uppercase',
			fontWeight: 500,
			color: mono.faint,
		},

		'.mnImpBody': { marginTop: 'clamp(40px, 5vw, 64px)', maxWidth: 620 },
	},

	'.mnBefore': {
		'.mnBeforeLede': { marginTop: 40, maxWidth: 900 },
		'.mnBeforeBody': { marginTop: 24, maxWidth: 620 },
	},

	// 도면 두 장을 같은 비율의 틀에 넣는다. 흰 종이는 multiply 로 그라운드에 녹인다.
	'.mnPlates': {
		marginTop: 'clamp(48px, 6vw, 80px)',
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: '48px 40px',

		'.mnPlate': {
			margin: 0,
			display: 'flex',
			flexDirection: 'column',
			gap: 12,
		},

		'.mnPlateFrame': {
			border: `1px solid ${mono.line}`,
			padding: 'clamp(16px, 2.4vw, 32px)',
		},

		'.mnPlateImage': {
			display: 'block',
			width: '100%',
			maxWidth: '100%',
			aspectRatio: '4 / 5',
			objectFit: 'contain',
			mixBlendMode: 'multiply',
		},

		'.mnPlateCaption': {
			fontSize: 13,
			fontWeight: 300,
			color: mono.faint,
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
		},
	},

	'.mnRitual': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 4fr)',
		gap: 'clamp(32px, 6.6vw, 96px)',
		alignItems: 'center',

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 40,
		},
	},

	'.mnFigure': {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,

		'.mnFigureImage': {
			display: 'block',
			width: '100%',
			height: 280,
			objectFit: 'cover',
			filter: 'grayscale(1) contrast(1.05) brightness(1.02)',
		},
		'.mnFigureCaption': {
			fontSize: 12,
			letterSpacing: '.16em',
			textTransform: 'uppercase',
			color: mono.faint,
			fontWeight: 500,
		},
	},

	'.mnSectionKicker': {
		display: 'block',
		marginBottom: 56,
	},

	'.mnScreens': {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: '48px 40px',

		'.mnScreen': {
			'.mnScreenImage': {
				width: '100%',
				display: 'block',
				border: `1px solid ${mono.line}`,
			},
			'.mnScreenCaption': {
				fontSize: 13,
				fontWeight: 300,
				color: mono.faint,
				marginTop: 12,
			},
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
		},
	},

	'.mnRearImageView': {
		marginTop: 64,
		width: '100%',
		maxWidth: 1180,

		'.mnRearImage': {
			width: '100%',
			aspectRatio: '3.4 / 1',
			objectFit: 'cover',
			display: 'block',
			filter: 'drop-shadow(0 24px 40px rgba(20,24,32,0.12))',
		},
	},

	'.mnRearCaption': {
		marginTop: 48,
		fontSize: 15,
		fontWeight: 300,
		color: mono.faint,
	},

	// 서브 네비(72+56) 아래에 착지한다 (DESIGN_SYSTEM §6.4)
	'.mnSpecBand': {
		scrollMarginTop: 128,
	},

	'.mnSpecTable': {
		maxWidth: 1000,

		'.mnSpecGroup': {
			'& + .mnSpecGroup': { marginTop: 56 },
		},

		'.mnSpecGroupLabel': {
			display: 'block',
			paddingBottom: 14,
			borderBottom: `1px solid ${mono.ink}`,
			fontSize: 12,
			letterSpacing: '.2em',
			textTransform: 'uppercase',
			fontWeight: 500,
			color: mono.ink,
		},

		'.mnSpecRow': {
			display: 'grid',
			gridTemplateColumns: 'minmax(0, 300px) minmax(0, 1fr)',
			gap: 24,
			padding: '17px 0',
			borderBottom: `1px solid ${mono.line}`,
			alignItems: 'baseline',

			'.mnSpecLabel': {
				fontSize: 13,
				letterSpacing: '.14em',
				textTransform: 'uppercase',
				color: mono.faint,
				fontWeight: 500,
			},
			'.mnSpecValue': {
				fontSize: 16,
				color: mono.ink,
				lineHeight: 1.5,
				fontWeight: 300,
			},

			'@media (max-width: 700px)': {
				gridTemplateColumns: '1fr',
				gap: 6,
			},
		},
	},

	/* Editor 밴드의 보조 버튼. 블랙 Buy 와 겨루지 않게 고스트다 */
	/*
		에디터 둘. 데스크톱 화면과 폰이 같은 높이로 서고, 바닥을 맞춘다. 폰은 프레임 포함 투명 PNG
		라 테두리 없이, 데스크톱 캡처만 헤어라인. 900 아래에서는 세로로.
	*/
	'.mnEditors': {
		marginTop: 56,
		display: 'flex',
		alignItems: 'flex-end',
		justifyContent: 'center',
		gap: 'clamp(32px, 5vw, 72px)',
		width: '100%',

		'.mnEditor': {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			textDecoration: 'none',
			color: mono.ink,
			minWidth: 0,
		},
		'.mnEditorFrame': {
			height: 'clamp(240px, 32vw, 460px)',
			display: 'flex',
			alignItems: 'flex-end',
		},
		'.mnEditorImage': {
			height: '100%',
			width: 'auto',
			maxWidth: '100%',
			display: 'block',
			objectFit: 'contain',
		},
		'.mnEditorImageDesktop': {
			border: `1px solid ${mono.line}`,
		},
		'.mnEditorName': {
			marginTop: 20,
			fontSize: 15,
			fontWeight: 500,
			letterSpacing: '.01em',
		},
		'.mnEditorMeta': {
			marginTop: 6,
			fontSize: 13,
			fontWeight: 300,
			color: mono.faint,
		},

		'@media (max-width: 900px)': {
			flexDirection: 'column',
			alignItems: 'center',
			gap: 48,
			'.mnEditorFrame': {
				height: 'auto',
				width: '100%',
				justifyContent: 'center',
			},
			'.mnEditorImageDesktop': {
				width: '100%',
				height: 'auto',
			},
			'.mnEditorImagePhone': {
				width: 'min(220px, 60%)',
				height: 'auto',
			},
		},
	},

	'.mnEditorAction': {
		marginTop: 48,
	},

	'.mnClose': {
		padding: 'clamp(80px, 9.7vw, 140px) 0',

		'.mnCloseAction': {
			marginTop: 48,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			gap: 28,
		},
	},

	/*
		판매가. Buy 옆에 숫자 하나 — 크게(22px) 가늘게(200), 취소선·배지 없이. 이 페이지에서
		할인은 말하지 않는다. 둥근 값이 그대로 보이는 것이 품질 신호다.
	*/
	'.mnListPrice': {
		fontSize: 22,
		fontWeight: 200,
		lineHeight: 1,
		color: mono.ink,
		letterSpacing: '-0.01em',
		fontVariantNumeric: 'tabular-nums',
	},
})
